import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import * as Icons from '../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { EventSponsorshipRow } from '../hooks/useEventManagement';

/**
 * 協賛品から当選者未定の賞品枠を作るダイアログ。
 *
 * 協賛品 1 点につき賞品の授与を 1 行作る (点数が未入力の協賛品は 1 点)。割当済みの行数を引いた分だけ作るので、
 * 同じ協賛品を選び直しても二重にはならない。賞の名前はまとめて付け、特賞・1等などは後で行ごとに直す。
 */

// 1 行の幅 (選択 / 受付NO / 協賛元 / 商品名 / 点数 / 割当済 / 作成数)
const ROW_GRID = '36px 52px minmax(0, 1fr) minmax(0, 1.3fr) 48px 52px 52px';

const EventAwardSlotsDialog = React.memo(function EventAwardSlotsDialog({
  open,
  sponsorships,
  assignedBySponsorship,
  onClose,
  onCreate,
}: {
  open: boolean;
  sponsorships: EventSponsorshipRow[];
  // 協賛品ごとの割当済みの行数 (賞品の授与の sponsorship_id を数えたもの)
  assignedBySponsorship: Map<number, number>;
  onClose: () => void;
  onCreate: (sponsorshipIds: number[], awardType: '表彰' | '抽選', awardName: string) => Promise<boolean>;
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [awardType, setAwardType] = useState<'表彰' | '抽選'>('抽選');
  const [awardName, setAwardName] = useState('抽選');
  const [saving, setSaving] = useState(false);

  // 協賛品ごとの、これから作る行数 (点数 - 割当済み)
  const restById = useMemo(
    () => new Map(sponsorships.map((s) => [s.id, Math.max(0, (s.quantity ?? 1) - (assignedBySponsorship.get(s.id) ?? 0))])),
    [sponsorships, assignedBySponsorship],
  );
  const selectable = useMemo(() => sponsorships.filter((s) => (restById.get(s.id) ?? 0) > 0), [sponsorships, restById]);

  // 開いたときは、まだ枠を作れて商品名が入っている協賛品を選んでおく
  useEffect(() => {
    if (!open) return;
    setSelected(new Set(selectable.filter((s) => s.product_name).map((s) => s.id)));
    setAwardType('抽選');
    setAwardName('抽選');
    // 開いた時点の状態で初期化する (開いている間に一覧が変わっても選択は保つ)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allChecked = selectable.length > 0 && selectable.every((s) => selected.has(s.id));
  const someChecked = selectable.some((s) => selected.has(s.id));
  const total = selectable.reduce((sum, s) => sum + (selected.has(s.id) ? restById.get(s.id) ?? 0 : 0), 0);
  const canCreate = total > 0 && !!awardName.trim() && !saving;

  const handleCreate = async () => {
    if (!canCreate) return;
    // 一覧の並び (受付NO 順) のまま送り、その順に発表順を振る
    const ids = selectable.filter((s) => selected.has(s.id)).map((s) => s.id);
    setSaving(true);
    const ok = await onCreate(ids, awardType, awardName.trim());
    setSaving(false);
    if (ok) onClose();
  };

  const cellSx = { fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth disableScrollLock>
      <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icons.Redeem sx={{ fontSize: 18 }} />
        協賛品から賞品枠を作る
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
          協賛品 1 点につき当選者未定の賞品枠を 1 行作ります（点数が未入力の協賛品は 1 点）。割当済みの分は作りません。
          賞の名前（特賞・1等 など）と発表順は、作った後に行ごとに直せます。
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={awardType}
            onChange={(_, v) => {
              if (!v) return;
              // 賞の名前を区分の既定のまま使っていれば、区分に合わせて付け替える
              if (awardName === awardType) setAwardName(v);
              setAwardType(v);
            }}
            sx={{ '& .MuiToggleButton-root': { fontSize: '12px', fontWeight: 700, px: 1.5, py: 0.5 } }}
          >
            <ToggleButton value="抽選">抽選</ToggleButton>
            <ToggleButton value="表彰">表彰</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            label="賞の名前"
            required
            value={awardName}
            onChange={(e) => setAwardName(e.target.value)}
            sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '13px' }, '& .MuiInputLabel-root': { fontSize: '13px' } }}
            inputProps={{ maxLength: 100 }}
          />
        </Box>

        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: ROW_GRID,
              gap: 1,
              alignItems: 'center',
              px: 0.5,
              bgcolor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              '& > *': { fontSize: '11px', fontWeight: 700, color: '#64748b' },
            }}
          >
            <Checkbox
              size="small"
              checked={allChecked}
              indeterminate={someChecked && !allChecked}
              disabled={selectable.length === 0}
              onChange={() => setSelected(allChecked ? new Set() : new Set(selectable.map((s) => s.id)))}
            />
            <Box component="span">受付NO</Box>
            <Box component="span">協賛元</Box>
            <Box component="span">商品名</Box>
            <Box component="span" sx={{ textAlign: 'right' }}>点数</Box>
            <Box component="span" sx={{ textAlign: 'right' }}>割当済</Box>
            <Box component="span" sx={{ textAlign: 'right' }}>作成数</Box>
          </Box>
          <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
            {sponsorships.length === 0 && (
              <Typography sx={{ fontSize: '12px', color: '#94a3b8', p: 2 }}>協賛品が登録されていません</Typography>
            )}
            {sponsorships.map((s) => {
              const rest = restById.get(s.id) ?? 0;
              const checked = selected.has(s.id);
              return (
                <Box
                  key={s.id}
                  onClick={() => rest > 0 && toggle(s.id)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: ROW_GRID,
                    gap: 1,
                    alignItems: 'center',
                    px: 0.5,
                    borderBottom: '1px solid #f1f5f9',
                    cursor: rest > 0 ? 'pointer' : 'default',
                    color: rest > 0 ? 'inherit' : '#94a3b8',
                    '&:hover': rest > 0 ? { bgcolor: '#f8fafc' } : undefined,
                  }}
                >
                  <Checkbox size="small" checked={checked} disabled={rest === 0} onClick={(e) => e.stopPropagation()} onChange={() => toggle(s.id)} />
                  <Typography sx={{ ...cellSx, color: '#94a3b8' }}>{s.reception_no}</Typography>
                  <Typography sx={cellSx}>{s.company_name}</Typography>
                  <Typography sx={{ ...cellSx, color: s.product_name ? 'inherit' : '#94a3b8' }}>{s.product_name ?? '(商品名未入力)'}</Typography>
                  <Typography sx={{ ...cellSx, textAlign: 'right' }}>{s.quantity ?? '-'}</Typography>
                  <Typography sx={{ ...cellSx, textAlign: 'right' }}>{assignedBySponsorship.get(s.id) ?? 0}</Typography>
                  <Typography sx={{ ...cellSx, textAlign: 'right', fontWeight: 700, color: checked && rest > 0 ? '#d97706' : 'inherit' }}>
                    {checked ? rest : '-'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={STANDARD_TEXT_BUTTON_SX}>
          キャンセル
        </Button>
        <Button
          onClick={handleCreate}
          disabled={!canCreate}
          startIcon={saving ? <CircularProgress size={14} /> : <Icons.Redeem />}
          sx={STANDARD_CTA_BUTTON_SX}
        >
          {total.toLocaleString()} 件作成する
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default EventAwardSlotsDialog;
